import { Icon } from '../ui/Icon.js';

/**
 * "Who's actually crawling you" — the core "why GEO" insight, made concrete.
 *
 * Splits the major AI bots into two purpose groups: training crawlers (decide
 * whether the model knows you at all) vs. search & answer bots (decide whether
 * you're retrieved & cited right now). Static data, no fetch. Rendered below
 * the human-vs-bot diagram in the `llm-view` section.
 */

interface Bot {
  readonly bot: string;
  readonly vendor: string;
  readonly purpose: string;
}

const TRAINING_BOTS: readonly Bot[] = [
  { bot: 'GPTBot', vendor: 'OpenAI', purpose: 'Scrapes & aggregates long-term data to train foundation models' },
  { bot: 'ClaudeBot', vendor: 'Anthropic', purpose: 'Crawls web content to train Claude' },
  { bot: 'Google-Extended', vendor: 'Google', purpose: 'Controls whether crawled content trains Gemini' },
  { bot: 'CCBot', vendor: 'Common Crawl', purpose: 'Open web-scale crawl widely reused as training data' },
  { bot: 'Bytespider', vendor: 'ByteDance', purpose: 'Crawls for model training' },
] as const;

const SEARCH_BOTS: readonly Bot[] = [
  { bot: 'OAI-SearchBot', vendor: 'OpenAI', purpose: "Indexes sites to power ChatGPT's conversational search" },
  { bot: 'ChatGPT-User', vendor: 'OpenAI', purpose: "Fetches pages on demand when a user's ChatGPT browses" },
  { bot: 'Googlebot', vendor: 'Google', purpose: 'Search index that also feeds AI Overviews' },
  { bot: 'Bingbot', vendor: 'Microsoft', purpose: "Bing's index — powers Copilot" },
  { bot: 'PerplexityBot', vendor: 'Perplexity', purpose: 'Indexes content to answer & cite in Perplexity' },
] as const;

interface BotGroupProps {
  readonly title: string;
  readonly subtitle: string;
  readonly icon: React.ReactNode;
  readonly accent: 'sky' | 'emerald';
  readonly bots: readonly Bot[];
}

// Spec 250 (review P1-15): the two groups are told apart by their titles and
// icons; the sky and emerald headers were colour standing in for a label that
// was already there. Both groups share the paper palette.
const ACCENT = {
  sky: { header: 'bg-[var(--hub-color-surface-sunken)] border-[var(--hub-color-line)]', chip: 'hub-disc', title: 'text-[var(--hub-color-ink)]', subtitle: 'text-[var(--hub-color-ink-muted)]' },
  emerald: { header: 'bg-[var(--hub-color-surface-sunken)] border-[var(--hub-color-line)]', chip: 'hub-disc', title: 'text-[var(--hub-color-ink)]', subtitle: 'text-[var(--hub-color-ink-muted)]' },
} as const;

function BotGroup({ title, subtitle, icon, accent, bots }: BotGroupProps) {
  const c = ACCENT[accent];
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)]">
      <div className={`flex items-start gap-3 border-b px-4 py-3 ${c.header}`}>
        <span className={`shrink-0 ${c.chip}`}>
          {icon}
        </span>
        <div className="min-w-0">
          <h4 className={`text-sm font-semibold ${c.title}`}>{title}</h4>
          <p className={`text-xs ${c.subtitle}`}>{subtitle}</p>
        </div>
      </div>
      <ul className="divide-y divide-[var(--hub-color-line)]">
        {bots.map((b) => (
          <li key={b.bot} className="px-4 py-2.5">
            <div className="flex items-baseline gap-2">
              {/* A user-agent token is literal code (spec 250: mono only for that). */}
              <code className="hub-code font-semibold">{b.bot}</code>
              <span className="text-xs text-[var(--hub-color-ink-subtle)]">{b.vendor}</span>
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-[var(--hub-color-ink-muted)]">
              {b.purpose}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BotVendorsPanel() {
  return (
    <section data-testid="hiw-bot-vendors" className="space-y-4">
      <div className="hub-plate hub-plate--flat px-4 py-3">
        <p className="text-sm font-medium leading-relaxed text-[var(--hub-color-ink)]">
          Training bots decide if the model <strong>knows</strong> you; search &
          answer bots decide if you’re <strong>cited today</strong> — that gap is
          why GEO exists, and it’s what GEO Observatory measures.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <BotGroup
          title="Training crawlers"
          subtitle="decide whether the model knows you at all"
          icon={<Icon name="bx-data" />}
          accent="sky"
          bots={TRAINING_BOTS}
        />
        <BotGroup
          title="Search & answer bots"
          subtitle="decide whether you’re retrieved & cited right now"
          icon={<Icon name="bx-search" />}
          accent="emerald"
          bots={SEARCH_BOTS}
        />
      </div>

      <p className="text-xs text-[var(--hub-color-ink-subtle)]">
        Vendors add and rename these bots over time — this is a representative
        snapshot, not a full list.
      </p>
    </section>
  );
}
